import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/es.promise.js";
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Third party components
import { Card, CardContent, Container, Grid, Stack, Typography } from '@mui/material';

// Shared components
import LineLoader from "./LineLoader.js";
import SubHeader from "./SubHeader.js";
import Button from "./Button.js";
import LoadingSpinner from "./LoadingSpinner.js";
import ContainerWithCard from "./ContainerWithCard.js";
import AnyField from "./AnyField.js"; // Custom hooks
import { useDynamicForm } from "../hooks/index.js";
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from 'axios';

/**
 * @param {object} props
 * @param {string} props.formTitle - The title of the form
 * @param {string} props.headerTitle - The title of the header
 * @param {string} props.cancelUrl - The url to navigate to when the user cancels
 * @param {string} props.successUrl - The url to navigate to when the user successfully submits the form
 * @param {boolean} props.isEdit - Whether or not this is an edit form
 * @param {object} props.defaultValues - The default values for the form
 * @param {object} props.layoutOptions - The layout options for the form
 * @param {boolean} props.twoColumn - Whether or not to render the form in two columns
 * @param {string} props.domainUrl - The url for the domain
 * @param {string} props.unitLabel - The label for the unit
 * @param {string} props.helpText - The help text for the form
 * @param {string} props.submitUrl - The url to submit the form to
 * @param {function} props.formatPayload - A function to format the payload before submitting
 * @param {function} props.onSuccess - A function to call when the form is successfully submitted
 * @param {boolean} props.suppressSuccessToast - Whether or not to suppress the success toast
 * @param {boolean} props.suppressErrorToast - Whether or not to suppress the success toast
 * @param {function} props.formatSubmitMessage - A function to format the success toaster message
 * @param {function} props.formatSubmitError - A function to format the error message (sends the error as a parameter and true if it came from the server)
 * @returns
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const GenericForm = _ref => {
  let {
    formTitle,
    headerTitle,
    cancelUrl,
    successUrl,
    isEdit,
    defaultValues,
    layoutOptions = {},
    twoColumn = false,
    domainUrl,
    unitLabel,
    helpText,
    submitUrl,
    formatPayload,
    onSuccess,
    suppressSuccessToast,
    suppressErrorToast,
    formatSubmitMessage,
    formatSubmitError
  } = _ref;
  const [modifying, setModifying] = useState(false);
  const {
    sections,
    layoutLoading,
    control,
    reset,
    handleSubmit
  } = useDynamicForm(layoutOptions, defaultValues, domainUrl, setModifying);
  const nav = useNavigate();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const onSubmit = async data => {
    const payload = formatPayload(data);
    addOrUpdate(payload, isEdit, successUrl, cancelUrl);
  };
  const addOrUpdate = async (orgData, edit, successUrl, cancelUrl) => {
    setModifying(true);
    try {
      var _result$data;
      const result = await axios.post(submitUrl, orgData);
      if (result !== null && result !== void 0 && (_result$data = result.data) !== null && _result$data !== void 0 && _result$data.streamID) {
        if (!suppressSuccessToast) {
          const successMsg = formatSubmitMessage ? formatSubmitMessage(result) : "".concat(unitLabel, " successfully ").concat(edit ? 'updated' : 'created');
          enqueueSnackbar(successMsg, {
            variant: 'success'
          });
        }
        // If we have an onSuccess callback, call it otherwise navigate to the successUrl
        if (onSuccess) {
          onSuccess(result);
        } else {
          nav(successUrl || cancelUrl);
          setModifying(false);
          nav(successUrl || cancelUrl);
        }
      } else {
        setModifying(false);
        if (!suppressErrorToast) {
          const errorMsg = formatSubmitError ? formatSubmitError(result) : "Error ".concat(edit ? 'updating' : 'creating', " ").concat(unitLabel);
          enqueueSnackbar(errorMsg, {
            variant: 'error'
          });
        }
      }
    } catch (error) {
      if (!suppressErrorToast) {
        var _error$response, _error$response$data;
        // If we have a nice server error use it.
        const serverError = error === null || error === void 0 ? void 0 : (_error$response = error.response) === null || _error$response === void 0 ? void 0 : (_error$response$data = _error$response.data) === null || _error$response$data === void 0 ? void 0 : _error$response$data.error;
        // Sending a true flag to formatSubmitError indicates that the error came from the server
        const errorMsg = formatSubmitError ? formatSubmitError(error, true) : "Error ".concat(edit ? 'updating' : 'creating', " ").concat(unitLabel);
        enqueueSnackbar(serverError || errorMsg, {
          variant: 'error'
        });
      }
      setModifying(false);
    }
  };

  // onSubmit is not called if the form is invalid
  // so we need to manually check for this
  const preSubmit = evt => {
    handleSubmit(onSubmit)(evt);
  };
  const rendered = () => {
    if (layoutLoading) {
      return /*#__PURE__*/_jsx(ContainerWithCard, {
        children: /*#__PURE__*/_jsx(LineLoader, {
          message: "Loading..."
        })
      });
    }
    const theSection = twoColumn ? renderTwoColumnSection : renderFormSection;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SubHeader, {
        title: headerTitle,
        rightRender: () => /*#__PURE__*/_jsxs(Stack, {
          spacing: 2,
          direction: "row",
          justifyContent: "flex-end",
          children: [/*#__PURE__*/_jsx(Button, {
            color: "tertiary",
            href: cancelUrl,
            label: "Cancel"
          }), isEdit && /*#__PURE__*/_jsx(Button, {
            color: "primary",
            onClick: () => reset(),
            label: 'Reset'
          }), /*#__PURE__*/_jsx(Button, {
            onClick: preSubmit,
            children: isEdit ? 'Edit' : 'Save'
          })]
        })
      }), /*#__PURE__*/_jsx(LoadingSpinner, {
        isActive: modifying
      }), /*#__PURE__*/_jsx(Container, {
        sx: {
          position: 'relative',
          marginTop: '16px'
        },
        maxWidth: false,
        children: /*#__PURE__*/_jsxs(Card, {
          sx: {
            position: 'relative'
          },
          children: [/*#__PURE__*/_jsxs(CardContent, {
            sx: {
              paddingBottom: '0px'
            },
            children: [formTitle && /*#__PURE__*/_jsx(Typography, {
              variant: "sectionHeader",
              children: formTitle
            }), helpText && helpText()]
          }), /*#__PURE__*/_jsx("hr", {}), /*#__PURE__*/_jsx(CardContent, {
            children: /*#__PURE__*/_jsx("form", {
              children: sections.map((section, index) => theSection(section, control, index))
            })
          })]
        })
      })]
    });
  };
  return rendered();
};
GenericForm.propTypes = {
  formTitle: PropTypes.string,
  cancelUrl: PropTypes.string,
  successUrl: PropTypes.string,
  isEdit: PropTypes.bool,
  defaultValues: PropTypes.object,
  layoutOptions: PropTypes.shape({
    type: PropTypes.string,
    key: PropTypes.string,
    url: PropTypes.string
  }),
  suppressSuccessToast: PropTypes.bool,
  submitUrl: PropTypes.string.isRequired,
  formatPayload: PropTypes.func.isRequired,
  domainUrl: PropTypes.string,
  unitLabel: PropTypes.string
};
const renderFormSection = (section, control, index) => {
  return /*#__PURE__*/_jsxs("div", {
    children: [section.title && /*#__PURE__*/_jsx(Typography, {
      variant: "sectionHeader",
      children: section.title
    }), section.fields.map((field, fIndex) => {
      var _field$render;
      return /*#__PURE__*/_jsx(AnyField, {
        sx: {
          marginTop: fIndex ? '16px' : null
        },
        layout: field.render,
        control: control
      }, field === null || field === void 0 ? void 0 : (_field$render = field.render) === null || _field$render === void 0 ? void 0 : _field$render.name);
    })]
  }, index);
};
const renderTwoColumnSection = (section, control, index) => {
  const nextCol = Math.ceil(section.fields.length / 2);
  const leftCol = section.fields.slice(0, nextCol);
  const rightCol = section.fields.slice(nextCol);
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsxs(Grid, {
      container: true,
      spacing: {
        xs: 1,
        sm: 2,
        md: 4
      },
      children: [/*#__PURE__*/_jsx(Grid, {
        item: true,
        xs: 6,
        children: leftCol.map((field, fIndex) => {
          var _field$render2;
          return /*#__PURE__*/_jsx(AnyField, {
            sx: {
              marginTop: fIndex ? '16px' : null
            },
            layout: field.render,
            control: control
          }, "".concat(index, "-left-").concat(field === null || field === void 0 ? void 0 : (_field$render2 = field.render) === null || _field$render2 === void 0 ? void 0 : _field$render2.name));
        })
      }, "".concat(index, "-left")), /*#__PURE__*/_jsx(Grid, {
        item: true,
        xs: 6,
        children: rightCol.map((field, fIndex) => {
          var _field$render3;
          return /*#__PURE__*/_jsx(AnyField, {
            sx: {
              marginTop: fIndex ? '16px' : null
            },
            layout: field.render,
            control: control
          }, "".concat(index, "-right-").concat(field === null || field === void 0 ? void 0 : (_field$render3 = field.render) === null || _field$render3 === void 0 ? void 0 : _field$render3.name));
        })
      }, "".concat(index, "-right"))]
    })
  }, index);
};
export default GenericForm;